import { useEffect, useState } from "react";
import { usePaymentsOpen, EARLY_ACCESS_LABEL } from "../lib/billingStatus";
import { Link } from "react-router-dom";
import {
  CalendarDays, MessageSquare, ClipboardList, Sparkles, Eraser, Users,
  Archive, BookOpen, ArrowRight, Check, UserPlus,
} from "lucide-react";
import { toast } from "sonner";

import { ChalkLogoLockup, ChalkLogoMark } from "../components/chalk/Logo";
import { LegalFooter } from "../components/chalk/LegalLayout";
import ChalkDust from "../components/chalk/ChalkDust";
import { api } from "../lib/chalkApi";
import { TournamentSection } from "../components/chalk/TournamentMarketing";
import { SHOW_TOURNAMENT_MARKETING } from "../lib/featureFlags";
import { AudienceCards, PhoneShot, ShotSteps, TierBadge } from "../components/marketing/Blocks";
import { SHOTS } from "../components/marketing/features";

// ── ChalkOS brand tokens — chalkboard-sport palette. ──
const C = {
  greenDark:   "#1a3a2a",
  greenMid:    "#2E7D52",
  greenBright: "#3d9e68",
  greenChalk:  "#4db87a",
  cream:       "#f5f0e8",
  creamDark:   "#ede7d9",
  offWhite:    "#faf8f4",
  textDark:    "#1a1a1a",
  chalkWhite:  "rgba(255,255,255,0.92)",
  chalkMuted:  "rgba(255,255,255,0.72)",
  border:      "rgba(0,0,0,0.08)",
  borderDark:  "rgba(255,255,255,0.16)",
  muted:       "#5a5a5a",
};

const DISPLAY = "'Bebas Neue', Oswald, Impact, sans-serif";
const MARKER  = "'Permanent Marker', Caveat, cursive";
const BODY    = "'Barlow', system-ui, sans-serif";

export default function Landing() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Chalk OS — Everything your team needs. One app.";
    return () => { document.title = prev; };
  }, []);

  return (
    <div data-testid="landing-page" style={{ fontFamily: BODY, color: C.textDark, background: C.cream }}>
      <a href="#main" className="chalk-skip-link" data-testid="skip-link">Skip to main content</a>
      <TopBar />
      <main id="main" tabIndex={-1}>
        <Hero />
        <Problem />
        <HowItWorks />
        <Features />
        <WhoFor />
        {SHOW_TOURNAMENT_MARKETING && <TournamentSection />}
        <Pricing />
        <EmailCapture />
        <FinalCTA />
      </main>
      <LegalFooter />
    </div>
  );
}

// ── Nav ───────────────────────────────────────────────────────────────
export function TopBar() {
  return (
    <header style={{
      background: C.offWhite,
      borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{
        maxWidth: 1200, margin: "0 auto",
        padding: "16px 22px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        gap: 16,
      }}>
        <Link to="/" style={{ textDecoration: "none", flexShrink: 0 }} aria-label="Chalk OS home">
          <span className="chalk-hide-xs"><ChalkLogoLockup size={40} color={C.textDark} /></span>
          <span className="chalk-show-xs"><ChalkLogoMark size={40} /></span>
        </Link>
        <style>{".chalk-audnav { display: none; } @media (min-width: 1040px) { .chalk-audnav { display: inline-flex; } } .chalk-audstrip { display: flex; } @media (min-width: 1040px) { .chalk-audstrip { display: none; } } @media (max-width: 560px) { .chalk-audstrip { justify-content: space-between; padding: 0 8px 10px !important; } .chalk-audstrip a { padding: 8px 6px !important; font-size: 11px !important; letter-spacing: 0.06em !important; } .chalk-topnav { gap: 2px !important; } .chalk-topnav a { padding: 8px 8px !important; font-size: 11px !important; letter-spacing: 0.06em !important; } .chalk-topnav .chalk-hide-sm { display: none; } } .chalk-show-xs { display: none; } @media (max-width: 420px) { .chalk-hide-xs { display: none; } .chalk-show-xs { display: inline-block; } }"}</style>
        <nav className="chalk-topnav" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="chalk-audnav" style={{ gap: 2 }}>
            {AUD_LINKS.map(([to, label]) => <NavLink key={to} to={to} testId={`nav-${to.slice(1)}`}>{label}</NavLink>)}
          </span>
          {SHOW_TOURNAMENT_MARKETING && <NavLink to="/tournaments" testId="nav-tournaments">Tournaments</NavLink>}
          <NavLink to="/pricing" testId="nav-pricing" className="chalk-hide-sm">Pricing</NavLink>
          <NavLink to="/login" testId="nav-signin">Sign in</NavLink>
          <Link to="/login?mode=signup" data-testid="nav-cta" className="chalk-btn-athletic chalk-btn-athletic--dark" style={{ padding: "11px 18px" }}>
            Get Chalk OS
          </Link>
        </nav>
      </div>
      <nav aria-label="Who it's for" className="chalk-audstrip" data-testid="audience-strip"
           style={{ gap: 4, overflowX: "auto", padding: "0 14px 10px", maxWidth: 1200, margin: "0 auto", whiteSpace: "nowrap" }}>
        {AUD_LINKS.map(([to, label]) => <NavLink key={to} to={to} testId={`strip-${to.slice(1)}`}>{label}</NavLink>)}
      </nav>
    </header>
  );
}

const AUD_LINKS = [["/coaches", "Coaches"], ["/parents", "Parents"], ["/team-staff", "Team staff"], ["/leagues", "Leagues"]];

function NavLink({ to, children, testId, className }) {
  return (
    <Link
      to={to}
      className={className}
      data-testid={testId}
      style={{
        fontFamily: BODY, fontWeight: 700, fontSize: 13,
        letterSpacing: "0.1em", textTransform: "uppercase",
        color: C.textDark, textDecoration: "none",
        padding: "10px 14px",
      }}
    >
      {children}
    </Link>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section
      className="chalkboard-texture"
      style={{
        background: C.greenDark,
        color: C.chalkWhite,
        padding: "84px 22px 96px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <ChalkDecorations />
      <ChalkDust count={20} opacity={0.45} />
      <div className="chalkos-hero-grid" style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2 }}>
        <div className="chalk-fade-up">
          <span className="chalk-marker-eyebrow" data-testid="hero-eyebrow">For coaches, captains, parents &amp; leagues</span>
          <h1 style={{
            fontFamily: DISPLAY, fontWeight: 400,
            fontSize: "clamp(3.5rem, 7vw, 6rem)",
            lineHeight: 0.92, letterSpacing: "0.02em",
            textTransform: "uppercase",
            margin: "22px 0 6px",
            color: C.chalkWhite,
          }}>
            Everything your<br />team needs.
          </h1>
          <div style={{
            fontFamily: MARKER,
            fontSize: "clamp(3rem, 6.5vw, 5.2rem)",
            color: C.greenChalk,
            lineHeight: 1,
            margin: "8px 0 26px",
            transform: "rotate(-2deg)",
            transformOrigin: "left center",
            display: "inline-block",
          }}>
            One app.
          </div>
          <p style={{
            fontFamily: BODY, fontWeight: 400, fontSize: 16,
            lineHeight: 1.6, color: C.chalkMuted,
            maxWidth: 520, margin: "0 0 32px",
          }} data-testid="hero-supporting-copy">
            Stop managing your season through group texts, scattered schedules, spreadsheets, and missed updates. Chalk OS gives coaches, captains, parents, and leagues one place for schedules, communication, RSVPs, and team organization.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link to="/login?mode=signup" data-testid="hero-primary-cta" className="chalk-btn-athletic">
              <ClipboardList size={16} aria-hidden="true" /> Get Chalk OS
            </Link>
            <a href="#how-it-works" data-testid="hero-secondary-cta" className="chalk-btn-athletic chalk-btn-athletic--outline">
              See how it works
            </a>
          </div>
          <div style={{ fontSize: 12, color: C.chalkMuted, marginTop: 18, letterSpacing: "0.06em" }}>
            No credit card required.
          </div>
        </div>
        <div className="chalk-fade-up-delay">
          <HeroVisual />
        </div>
      </div>
      <style>{`
        .chalkos-hero-grid {
          display: flex; flex-direction: column;
          gap: 48px; align-items: stretch;
        }
        @media (min-width: 880px) {
          .chalkos-hero-grid {
            display: grid;
            grid-template-columns: 1.15fr 0.85fr;
            gap: 56px;
            align-items: center;
          }
        }
      `}</style>
    </section>
  );
}

// Scattered chalk X / O / arrow marks behind the hero text.
function ChalkDecorations() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1 }}
    >
      {/* X marks */}
      <g stroke={C.chalkWhite} strokeWidth="0.4" strokeLinecap="round" opacity="0.16">
        <line x1="6" y1="18" x2="9" y2="22" />
        <line x1="9" y1="18" x2="6" y2="22" />
        <line x1="92" y1="72" x2="96" y2="76" />
        <line x1="96" y1="72" x2="92" y2="76" />
        <line x1="48" y1="88" x2="51" y2="92" />
        <line x1="51" y1="88" x2="48" y2="92" />
      </g>
      {/* O marks */}
      <g fill="none" stroke={C.greenChalk} strokeWidth="0.4" opacity="0.22">
        <circle cx="78" cy="14" r="2.5" />
        <circle cx="14" cy="70" r="2" />
        <circle cx="34" cy="92" r="1.7" />
      </g>
      {/* dashed arrows */}
      <g fill="none" stroke={C.chalkWhite} strokeWidth="0.35" strokeLinecap="round" opacity="0.18" strokeDasharray="0.8 0.8">
        <path d="M 18 30 Q 28 22 36 32" />
        <path d="M 60 78 Q 70 70 82 82" />
      </g>
      <g fill="none" stroke={C.greenChalk} strokeWidth="0.4" opacity="0.30" strokeLinecap="round" strokeLinejoin="round">
        <path d="M 33 34 L 36 32 L 36 35" />
        <path d="M 79 81 L 82 82 L 81 85" />
      </g>
    </svg>
  );
}

function HeroVisual() {
  return (
    <div data-testid="hero-visual" className="chalkos-hero-shots">
      <div className="chalkos-hero-shot chalkos-hero-shot--back"><PhoneShot shot={SHOTS.clipboard} testId="hero-shot-parent" tilt={4} /></div>
      <div className="chalkos-hero-shot chalkos-hero-shot--front"><PhoneShot shot={SHOTS.chalkboard} testId="hero-shot-coach" tilt={-3} /></div>
      <style>{`
        .chalkos-hero-shots { position: relative; display: flex; justify-content: center; }
        .chalkos-hero-shot { width: 62%; max-width: 270px; }
        .chalkos-hero-shot--back { position: absolute; right: 2%; top: 30px; opacity: 0.92; }
        .chalkos-hero-shot--front { position: relative; margin-right: 26%; z-index: 2; }
        @media (max-width: 879px) { .chalkos-hero-shot--back { display: none; } .chalkos-hero-shot--front { margin-right: 0; width: 72%; } }
      `}</style>
    </div>
  );
}

// ── Section heading pair ──────────────────────────────────────────────
export function SectionHead({ title, subtitle, dark = false, align = "center" }) {
  return (
    <div style={{ textAlign: align }}>
      <h2 style={{
        fontFamily: DISPLAY, fontWeight: 400,
        fontSize: "clamp(2.5rem, 5vw, 4rem)",
        lineHeight: 0.95, letterSpacing: "0.02em",
        textTransform: "uppercase",
        color: dark ? C.chalkWhite : C.textDark,
        margin: 0,
      }}>{title}</h2>
      {subtitle && (
        <div style={{
          fontFamily: MARKER,
          fontSize: "clamp(1.6rem, 3.2vw, 2.4rem)",
          color: dark ? C.greenChalk : C.greenMid,
          margin: "8px 0 0",
          lineHeight: 1,
        }}>{subtitle}</div>
      )}
      <span style={{
        display: "block", width: 160, height: 3,
        background: dark ? C.greenChalk : C.greenMid,
        margin: align === "center" ? "18px auto 0" : "18px 0 0",
        borderRadius: 2,
      }} />
    </div>
  );
}

// ── Problem ────────────────────────────────────────────────────────────
function Problem() {
  const items = [
    { eyebrow: "GROUP TEXT CHAOS", body: "Schedules buried in 200 messages." },
    { eyebrow: "PAPER HANDOUTS",   body: "Half the team forgot where practice moved." },
    { eyebrow: "TOO MANY APPS",    body: "Parents checking five places for one answer." },
  ];
  return (
    <section style={{ ...sectionBase, background: C.offWhite }}>
      <SectionHead title="You're not coaching." subtitle="You're managing chaos." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16, marginTop: 48 }}>
        {items.map((it) => (
          <div key={it.eyebrow} className="chalk-marketing-card" style={cardLight}>
            <div style={eyebrowStyle}>{it.eyebrow}</div>
            <div style={cardTitleStyle}>{it.body}</div>
          </div>
        ))}
      </div>
      <p style={{ marginTop: 32, fontFamily: BODY, fontSize: 17, color: C.muted, textAlign: "center" }}>
        Chalk OS puts everything in one place.
      </p>
    </section>
  );
}

// ── How it works ───────────────────────────────────────────────────────
const HOW_STEPS = [
  { title: "Build your team", tier: "free", shot: SHOTS.roster,
    body: "Add players or import a list, then share a join code or invite parents by email." },
  { title: "Run your season", tier: "free", shot: SHOTS.chalkboard,
    body: "Practices, games, RSVPs and Chalk Talk from one Chalkboard. Overlapping events get a warning before families see them." },
  { title: "Change plans in seconds", tier: "board", shot: SHOTS.bulk,
    body: "Erase and Redraw one event free, or a whole date range on The Chalkboard. Each family gets one combined alert." },
  { title: "Keep families organized", tier: "free", shot: SHOTS.clipboard,
    body: "Parents see every kid's next event, RSVP in one tap and get a banner the moment something changes." },
];

function HowItWorks() {
  return (
    <section id="how-it-works" style={{ ...sectionBase, background: C.cream }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <SectionHead title="How it works." subtitle="Real screens from the live app." />
        <ShotSteps steps={HOW_STEPS} idPrefix="how" />
        <div style={{ marginTop: 48, textAlign: "center" }}>
          <Link to="/login?mode=signup" data-testid="how-cta" className="chalk-btn-athletic chalk-btn-athletic--dark">
            Start free <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Features grid ──────────────────────────────────────────────────────
function Features() {
  const feats = [
    { Icon: CalendarDays,   name: "The Chalkboard",       tier: "free",  body: "Your season at a glance: the next play, who's going, and one-tap actions." },
    { Icon: ClipboardList,  name: "The Clipboard",        tier: "free",  body: "What every family sees: each kid's next event, RSVPs and change banners." },
    { Icon: MessageSquare,  name: "Chalk Talk",           tier: "free",  body: "Announcements and polls with push and email alerts and read counts." },
    { Icon: BookOpen,       name: "The Playbook",         tier: "free",  body: "Every game and practice on one timeline, with a warning when events overlap." },
    { Icon: Eraser,         name: "Erase and Redraw",     tier: "free",  body: "Cancel, delay or move one event and every family is notified. Bulk date-range edits on The Chalkboard." },
    { Icon: Sparkles,       name: "Draw It Up",           tier: "board", body: "Chalk OS drafts coach-voice updates in seconds. You review and post." },
    { Icon: Users,          name: "Volunteer Management", tier: "board", body: "Rotate snack duty in one tap. Parents claim open duties first-come." },
    { Icon: UserPlus,       name: "Team Staff",           tier: "free",  body: "Invite a Team Mom, Dad or assistant coach and choose exactly what they can do." },
    { Icon: Archive,        name: "Season Wrap-up",       tier: "free",  body: "Wrap a season and start the next one with your roster carried over." },
  ];
  return (
    <section style={{ ...sectionBase, background: C.offWhite }}>
      <SectionHead title="Built for the way" subtitle="coaches actually work." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, marginTop: 48 }}>
        {feats.map((f) => (
          <div key={f.name} className="chalk-marketing-card" style={cardLight} data-testid={`feature-${f.name.toLowerCase().replace(/\s+/g, "-")}`}>
            <div style={{
              background: C.cream, width: 40, height: 40, borderRadius: 10,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: C.greenMid,
            }}>
              <f.Icon size={20} aria-hidden="true" />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 14 }}>
              <div style={{ ...cardTitleStyle, marginTop: 0, fontSize: 22 }}>{f.name}</div>
              <TierBadge tier={f.tier} />
            </div>
            <p style={cardBodyStyle}>{f.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Who for ───────────────────────────────────────────────────────────
function WhoFor() {
  return (
    <section style={{ ...sectionBase, background: C.cream }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <SectionHead title="Who it's for." subtitle="Youth teams, adult teams, leagues." />
        <AudienceCards />
      </div>
    </section>
  );
}


// ── Pricing ───────────────────────────────────────────────────────────
function Pricing() {
  return (
    <section id="pricing" style={{ ...sectionBase, background: C.cream }}>
      <SectionHead title="Simple pricing." subtitle="Start free. Upgrade when ready." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 16, marginTop: 48 }}>
        <PriceCard name="Pickup"          price="Free"        cta="Start free"     body="One team of up to 15 players: schedule, RSVPs, Chalk Talk, conflict warnings, and single-event Erase and Redraw." />
        <PriceCard name="The Clipboard+"  price="$7.99"       interval="/month"    cta="Upgrade"        paid body="For parents: Family Sports Dashboard, conflict detection across your kids' teams, full message history, calendar subscription." />
        <PriceCard name="The Chalkboard"  price="$11.99"      interval="/month"    cta="Get Chalkboard" paid body="For coaches: bulk Erase and Redraw, Draw It Up AI, volunteers, recurring events, up to 3 team staff." recommended />
        <PriceCard name="League OS"       price="Contact us"  cta="Talk to us"     body="League-wide broadcasts, multi-team dashboards, custom support." sales />
      </div>
    </section>
  );
}

function PriceCard({ name, price, interval, body, cta, recommended, sales, paid }) {
  const open = usePaymentsOpen();
  const early = paid && !open;
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div
      data-testid={`price-${slug}`}
      style={{
        background: recommended ? "#1f3b2d" : C.offWhite,
        color: recommended ? C.chalkWhite : C.textDark,
        border: recommended ? "none" : `1px solid ${C.border}`,
        padding: 24, borderRadius: 16,
        boxShadow: recommended ? "0 22px 50px rgba(26,58,42,0.32)" : "none",
        position: "relative",
      }}
    >
      {recommended && (
        <span style={{
          position: "absolute", top: -12, left: 22,
          background: C.greenMid, color: "#fff",
          fontFamily: BODY, fontWeight: 700, fontSize: 10,
          padding: "5px 10px", borderRadius: 6,
          letterSpacing: "0.16em", textTransform: "uppercase",
        }}>Recommended</span>
      )}
      <div style={{
        fontFamily: DISPLAY, fontSize: 26, letterSpacing: "0.02em",
        textTransform: "uppercase", lineHeight: 1,
      }}>{name}</div>
      <div style={{ marginTop: 12, display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontFamily: DISPLAY, fontSize: 44, letterSpacing: "0.02em", lineHeight: 1 }}>{price}</span>
        {interval && <span style={{ fontFamily: BODY, fontSize: 14, opacity: 0.7 }}>{interval}</span>}
      </div>
      <p style={{
        fontFamily: BODY, fontSize: 14, lineHeight: 1.55,
        marginTop: 12, color: recommended ? "rgba(255,255,255,0.72)" : C.muted,
      }}>{body}</p>
      {sales ? (
        <a href="mailto:support@getchalkos.com?subject=League OS"
           data-testid="price-cta-league"
           className="chalk-btn-athletic chalk-btn-athletic--dark"
           style={{ marginTop: 18, width: "100%" }}>
          {cta} <ArrowRight size={14} />
        </a>
      ) : (
        <Link to="/login?mode=signup"
              data-testid={`price-cta-${slug}`}
              className={recommended ? "chalk-btn-athletic" : "chalk-btn-athletic chalk-btn-athletic--dark"}
              style={{ marginTop: 18, width: "100%", background: recommended ? "#fff" : undefined, color: recommended ? C.greenDark : undefined }}>
          {early ? EARLY_ACCESS_LABEL : cta} <ArrowRight size={14} />
        </Link>
      )}
      {early && <div data-testid={`price-early-note-${slug}`} style={{ fontFamily: BODY, fontSize: 12, marginTop: 10, opacity: 0.75 }}>Paid plans open soon.</div>}
    </div>
  );
}

// ── Email capture ─────────────────────────────────────────────────────
function EmailCapture() {
  const [first, setFirst] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.captureLead({ first_name: first, email, source: "landing" });
      setDone(true);
      toast.success("You're on the list. Talk soon, Coach.");
    } catch (err) {
      toast.error(err.message || "Couldn't add you. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ ...sectionBase, background: C.offWhite }}>
      <div style={{
        background: C.cream,
        border: `1px solid ${C.border}`,
        borderRadius: 18,
        padding: "52px 24px",
        maxWidth: 720, margin: "0 auto",
        textAlign: "center",
      }}>
        <span className="chalk-marker-eyebrow">Stay in the loop</span>
        <h2 style={{
          fontFamily: DISPLAY, fontWeight: 400,
          fontSize: "clamp(2rem, 4vw, 2.8rem)",
          letterSpacing: "0.02em", textTransform: "uppercase",
          lineHeight: 0.98, margin: "20px 0 0", color: C.textDark,
        }}>Get product updates.</h2>
        {done ? (
          <div style={{
            marginTop: 24, fontFamily: BODY, fontSize: 16, color: C.greenMid,
            display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 600,
          }}>
            <Check size={18} /> Thanks, {first || "Coach"}. We&apos;ll email you when something new ships.
          </div>
        ) : (
          <form onSubmit={submit} style={{
            marginTop: 26,
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 170px), 1fr))",
            gap: 10, maxWidth: 580, margin: "26px auto 0",
          }}>
            <input
              required value={first} onChange={(e) => setFirst(e.target.value)}
              placeholder="First name" data-testid="lead-first"
              aria-label="First name" style={inputStyle}
            />
            <input
              required value={email} onChange={(e) => setEmail(e.target.value)}
              type="email" placeholder="you@example.com" data-testid="lead-email"
              aria-label="Email" style={inputStyle}
            />
            <button type="submit" disabled={busy} data-testid="lead-submit"
                    className="chalk-btn-athletic chalk-btn-athletic--dark"
                    style={{ opacity: busy ? 0.7 : 1, padding: "12px 18px" }}>
              {busy ? "Sending…" : "Send me updates"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

// ── Final CTA ─────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section
      className="chalkboard-texture"
      style={{ background: C.greenDark, color: C.chalkWhite, padding: "88px 22px", textAlign: "center", position: "relative", overflow: "hidden" }}
    >
      <ChalkDecorations />
      <ChalkDust count={14} opacity={0.4} />
      <div style={{ position: "relative", zIndex: 2, maxWidth: 840, margin: "0 auto" }}>
        <SectionHead title="One app. Every team." subtitle="Every season." dark />
        <p style={{ color: C.chalkMuted, marginTop: 18, fontSize: 17, fontFamily: BODY }}>
          Start free. Upgrade when you&apos;re ready.
        </p>
        <Link to="/login?mode=signup" data-testid="final-cta" className="chalk-btn-athletic" style={{ marginTop: 28 }}>
          <ClipboardList size={16} aria-hidden="true" /> Get Chalk OS
        </Link>
      </div>
    </section>
  );
}

// ── Styling tokens ────────────────────────────────────────────────────
const sectionBase = { padding: "88px 22px", width: "100%" };
const cardLight = {
  background: "#fff",
  border: `1px solid ${C.border}`,
  borderRadius: 16,
  padding: 22,
};
const eyebrowStyle = {
  fontFamily: BODY,
  fontWeight: 700, fontSize: 11,
  letterSpacing: "0.14em", textTransform: "uppercase",
  color: C.greenChalk,
};
const cardTitleStyle = {
  fontFamily: DISPLAY, fontSize: 22,
  letterSpacing: "0.02em", textTransform: "uppercase",
  color: C.textDark, lineHeight: 1.05, marginTop: 12,
};
const cardBodyStyle = {
  fontFamily: BODY, fontSize: 14,
  lineHeight: 1.55, color: C.muted, marginTop: 8,
};
const inputStyle = {
  background: "#fff", border: `1px solid ${C.border}`,
  padding: "12px 14px",
  borderRadius: 9, fontSize: 16, color: C.textDark,
  outline: "none", fontFamily: BODY,
};

export { C, DISPLAY, MARKER, BODY, sectionBase, cardLight, eyebrowStyle, cardTitleStyle, cardBodyStyle };
